import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { getErrorMessage } from '@/lib/api'
import { formatClock, formatDay, minutesLabel, punchDate, todayIsoDate, type AttendancePunch } from '@/pages/attendance/data'

/** "YYYY-MM-DDTHH:MM" in the org's wall clock, as a datetime-local input expects. */
function wallClockNow(timeZone?: string) {
  try {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-CA', {
        timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
      }).formatToParts(new Date()).map((part) => [part.type, part.value]),
    )
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`
  } catch {
    const now = new Date()
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset())
    return now.toISOString().slice(0, 16)
  }
}

function wallClockMinutes(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value)
  if (!match) return null
  const [, year, month, day, hour, minute] = match.map(Number)
  return Date.UTC(year, month - 1, day, hour, minute) / 60000
}

export function CloseAttendanceDialog({
  punch,
  employeeName,
  timeZone,
  isPending = false,
  onOpenChange,
  onSubmit,
}: {
  punch: AttendancePunch | null
  employeeName: string
  timeZone?: string
  isPending?: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (checkOut: string) => Promise<unknown>
}) {
  const checkIn = String(punch?.check_in_local || punch?.check_in || '')
  const checkInValue = checkIn.slice(0, 16)
  const startedToday = punch ? punchDate(punch) === todayIsoDate(timeZone) : false
  const [checkOut, setCheckOut] = useState('')

  useEffect(() => {
    if (punch) setCheckOut(startedToday ? wallClockNow(timeZone) : '')
  }, [punch, startedToday, timeZone])

  const start = wallClockMinutes(checkInValue)
  const end = wallClockMinutes(checkOut)
  const worked = start != null && end != null ? end - start : null
  const invalid = worked != null && worked <= 0
  const future = Boolean(checkOut) && checkOut > wallClockNow(timeZone)

  const submit = async () => {
    if (!checkOut) {
      toast.error('Pick the check-out time')
      return
    }
    if (invalid) {
      toast.error('Check-out must be after the check-in time')
      return
    }
    if (future) {
      toast.error('Check-out cannot be in the future')
      return
    }
    try {
      await onSubmit(checkOut)
      onOpenChange(false)
    } catch (error) {
      toast.error(getErrorMessage(error))
    }
  }

  return (
    <Dialog open={Boolean(punch)} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Close attendance</DialogTitle>
          <DialogDescription>
            Record the check-out for {employeeName}&apos;s open punch.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
            <div className="text-xs text-slate-500">Checked in</div>
            <div className="font-medium tabular-nums text-slate-900">{formatDay(checkIn)} · {formatClock(checkIn)}</div>
            {!startedToday ? <div className="mt-1 text-xs text-amber-700">This punch was left open from an earlier day — set when they actually left.</div> : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="close-check-out">Check-out time</Label>
            <Input
              id="close-check-out"
              type="datetime-local"
              value={checkOut}
              min={checkInValue}
              max={wallClockNow(timeZone)}
              onChange={(event) => setCheckOut(event.target.value)}
            />
            {invalid ? (
              <p className="text-xs text-destructive">Check-out must be after the check-in time.</p>
            ) : future ? (
              <p className="text-xs text-destructive">Check-out cannot be in the future.</p>
            ) : worked != null ? (
              <p className="text-xs text-slate-500">Worked {minutesLabel(worked)}</p>
            ) : null}
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>Cancel</Button>
          <Button type="button" onClick={() => void submit()} disabled={isPending || !checkOut || invalid || future}>
            {isPending ? 'Closing...' : 'Close attendance'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
